<template>
  <div class="search-box apartments-search-box" :class="hasErrorsClass">
    <autocomplete
      :id="config.testing.FRONT_SEARCH_LOCATION_INPUT"
      :resultsId="config.testing.FRONT_SEARCH_LOCATION_RESULTS"
      :useLabel="true"
      className="input-wrapper"
    ></autocomplete>
    <div class="input-wrapper">
      <label :for="areaMinId">Pinta-ala vähintään</label>
      <input
        :id="areaMinId"
        type="number"
        class="form-input"
        v-model="livingAreaMin"
        v-on:keypress.enter="searchApartments()"
        :aria-describedby="areaMinErrorId"
        :aria-invalid="!isAreaValid"
      />
      <input-error :id="areaMinErrorId" :show="!isAreaValid"
        >Pinta-alan oltava väliltä {{ configAreaMin }}-{{ configAreaMax }}</input-error
      >
    </div>
    <div v-if="mode !== 'rent'" class="input-wrapper">
      <label :for="priceMaxId">Hinta enintään</label>
      <input
        :id="priceMaxId"
        type="number"
        class="form-input"
        v-model="priceMax"
        v-on:focus="inputFocus($event)"
        v-on:keypress.enter="searchApartments()"
        :aria-describedby="priceMaxErrorId"
        :aria-invalid="!isPriceValid"
      />
      <input-error :id="priceMaxErrorId" :show="!isPriceValid"
        >Hinnan oltava väliltä {{ configPriceMin }}-{{ configPriceMax }}</input-error
      >
    </div>
    <div v-if="mode === 'rent'" class="input-wrapper">
      <label :for="rentMaxId">Vuokra enintään</label>
      <input
        :id="rentMaxId"
        type="number"
        class="form-input"
        v-model="rentMax"
        v-on:focus="inputFocus($event)"
        v-on:keypress.enter="searchApartments()"
        :aria-describedby="rentMaxErrorId"
        :aria-invalid="!isRentValid"
      />
      <input-error :id="rentMaxErrorId" :show="!isRentValid"
        >Vuokran oltava väliltä {{ configRentMin }}-{{ configRentMax }}</input-error
      >
    </div>
    <form-button
      :disabled="!isFormValid"
      :id="config.testing.FRONT_SEARCH_BUTTON"
      theme="primary no-pad"
      @click.native="searchApartments()"
      aria-label="hae"
    >
      <i class="search-icon icon-search"></i>
    </form-button>
  </div>
</template>

<script lang="ts">
import config from '@/config';
import FormInput from '../form-input/form-input.vue';
import InputError from '../form/input-error.vue';
import Autocomplete from '../autocomplete/autocomplete.vue';
import router from '../../router';
import * as _ from 'lodash-es';
import utils from '@/app/utils/utils';
import { search, listing } from '@/app/routes';
import { defineAsyncComponent, defineComponent } from 'vue';
import searchUtils from '@/app/utils/search-utils';
import type {
  CityNameType,
  CountyNameType,
  DistrictNameType,
  RegionNameType,
  StreetNameType,
} from '@/types/schemaTypes';

export default defineComponent({
  name: 'apartments-search-box',
  data: () => ({
    config,
    livingAreaMin: undefined as number | undefined,
    priceMax: undefined as number | undefined,
    rentMax: undefined as number | undefined,
    areaMinId: config.testing.FRONT_SEARCH_AREA_INPUT,
    areaMinErrorId: config.testing.FRONT_SEARCH_AREA_INPUT + '-error',
    priceMaxId: config.testing.FRONT_SEARCH_PRICE_INPUT,
    priceMaxErrorId: config.testing.FRONT_SEARCH_PRICE_INPUT + '-error',
    rentMaxId: config.testing.FRONT_SEARCH_PRICE_INPUT,
    rentMaxErrorId: config.testing.FRONT_SEARCH_PRICE_INPUT + '-error',
    configAreaMin: config.FILTER_LIMITS.AREA_MIN + 1,
    configAreaMax: config.FILTER_LIMITS.AREA_MAX - 1,
    configPriceMin: config.FILTER_LIMITS.PRICE_MIN + 1,
    configPriceMax: config.FILTER_LIMITS.PRICE_MAX - 1,
    configRentMin: config.FILTER_LIMITS.RENT_MIN + 1,
    configRentMax: config.FILTER_LIMITS.RENT_MAX - 1,
    cities: [] as CityNameType[],
    counties: [] as CountyNameType[],
    districts: [] as DistrictNameType[],
    streets: [] as StreetNameType[],
    regions: [] as RegionNameType[],
    postalCodes: [] as string[],
    listingId: null as null | string,
    searchRoute: search,
    searchFilters: {},
    keys: {
      cityId: 'cityId',
      countyId: 'countyId',
      districtId: 'districtId',
      streetId: 'streetId',
      regionId: 'regionId',
      postalCode: 'postalCode',
      mode: 'mode',
      livingAreaMin: 'livingAreaMin',
      priceMax: 'priceMax',
      rentMax: 'rentMax',
      featureGroup: 'featureGroup',
    },
  }),
  props: {
    mode: {
      type: String,
    },
  },
  methods: {
    inputFocus(event: FocusEvent) {
      utils.inputFocusOnAndroid(event);
    },
    searchApartments() {
      if (!this.isFormValid) {
        return;
      }
      if (this.listingId) {
        void router.push(`${listing.path}/${this.listingId}`);
        return;
      }

      const target = this.mode ? this.rental : this.sale;

      // remove old filters and reset result scroll offset
      this.searchFilters = {};

      // Location
      this.counties?.map((c) => {
        this.searchFilters = searchUtils.updateFiltersObj(this.searchFilters, { countyId: c.countyId }, true);
      });
      this.cities?.map((c) => {
        this.searchFilters = searchUtils.updateFiltersObj(this.searchFilters, { cityId: c.cityId }, true);
      });
      this.districts?.map((d) => {
        this.searchFilters = searchUtils.updateFiltersObj(this.searchFilters, { districtId: d.districtId }, true);
      });
      this.streets?.map((s) => {
        this.searchFilters = searchUtils.updateFiltersObj(this.searchFilters, { streetId: s.streetId }, true);
      });
      this.regions?.map((r) => {
        this.searchFilters = searchUtils.updateFiltersObj(this.searchFilters, { regionId: r.regionId }, true);
      });
      this.postalCodes?.map((p) => {
        this.searchFilters = searchUtils.updateFiltersObj(this.searchFilters, { postalCode: p }, true);
      });

      // Living area
      if (!!this.livingAreaMin && this.livingAreaMin !== 0) {
        const area = _.toNumber(this.livingAreaMin);
        this.searchFilters = searchUtils.updateFiltersObj(this.searchFilters, { livingAreaMin: area });
      }

      // Price max
      if (this.priceMax) {
        this.searchFilters = searchUtils.updateFiltersObj(this.searchFilters, { priceMax: this.priceMax });
      }

      if (this.rentMax) {
        this.searchFilters = searchUtils.updateFiltersObj(this.searchFilters, { rentMax: this.rentMax });
      }

      if (Object.keys(this.searchFilters).length > 0) {
        void router.push({ path: target, query: this.searchFilters });
      } else {
        void router.push({ path: target });
      }
    },
  },
  mounted() {
    this.$bus.$on('autocompleteDistrictSelected', (district) => {
      this.districts.push(district);
    });
    this.$bus.$on('autocompleteStreetSelected', (street) => {
      this.streets.push(street);
    });
    this.$bus.$on('autocompleteRegionSelected', (region) => {
      this.regions.push(region);
    });
    this.$bus.$on('autocompleteCitySelected', (city) => {
      this.cities.push(city);
    });
    this.$bus.$on('autocompleteCountySelected', (county) => {
      this.counties.push(county);
    });
    this.$bus.$on('autocompletePostalCodeSelected', (postalCode) => {
      this.postalCodes.push(postalCode);
    });
    this.$bus.$on('clearAutocompleteLocations', () => {
      this.districts = [];
      this.streets = [];
      this.regions = [];
      this.cities = [];
      this.postalCodes = [];
    });
    this.$bus.$on('triggerSearchApartments', () => {
      this.searchApartments();
    });
    this.$bus.$on('autocompleteListingFound', (listingId) => {
      this.listingId = listingId;
    });
  },
  computed: {
    sale() {
      return search.sale.routes.find((route) => route.id === config.testing.NAVIGATION.BUYING.APARTMENTS)?.path;
    },
    rental() {
      return search.rental.routes.find((route) => route.id === config.testing.NAVIGATION.RENTING.APARTMENTS)?.path;
    },
    hasErrorsClass() {
      return 'has-errors';
    },
    isAreaValid() {
      return (
        !this.livingAreaMin || (this.livingAreaMin >= this.configAreaMin && this.livingAreaMin <= this.configAreaMax)
      );
    },
    isPriceValid() {
      return !this.priceMax || (this.priceMax >= this.configPriceMin && this.priceMax <= this.configPriceMax);
    },
    isRentValid() {
      return !this.rentMax || (this.rentMax >= this.configRentMin && this.rentMax <= this.configRentMax);
    },
    isFormValid() {
      if (this.mode === 'rent') {
        return this.isAreaValid && this.isRentValid;
      }
      return this.isAreaValid && this.isPriceValid;
    },
  },
  beforeUnmount() {
    this.$bus.$off('autocompleteDistrictSelected');
    this.$bus.$off('autocompleteStreetSelected');
    this.$bus.$off('autocompleteRegionSelected');
    this.$bus.$off('autocompleteCitySelected');
    this.$bus.$off('autocompleteCountySelected');
    this.$bus.$off('autocompletePostalCodeSelected');
    this.$bus.$off('clearAutocompleteLocations');
    this.$bus.$off('triggerSearchApartments');
    this.$bus.$off('autocompleteListingFound');
  },
  components: {
    FormButton: defineAsyncComponent(() => import('@/app/components/form-button/form-button.vue')),
    FormInput,
    InputError,
    Autocomplete,
  },
});
</script>
